import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';
import { Plus, Calendar, Edit2, Trash2, Save, X, Check, Settings, Clock, Filter, User } from 'lucide-react';
import { toast } from '../lib/toast';
import { Database } from '../types/supabase';
import DailyPlannerSettings from '../components/DailyPlannerSettings';

type Task = Database['public']['Tables']['tasks']['Row'];
type Project = Database['public']['Tables']['projects']['Row'];
type TeamMember = Database['public']['Tables']['team_members']['Row'];

interface DailyPlannerTask {
  id: string;
  taskid: string;
  title: string;
  status: string;
  assigned_to?: string;
  notes: string;
  date: string;
  user_id: string;
  created_at: string;
}

interface DailyPlannerProps {
  navigate: (to: string) => void;
}

export default function DailyPlanner({ navigate }: DailyPlannerProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dailyTasks, setDailyTasks] = useState<DailyPlannerTask[]>([]);
  const [customStatuses, setCustomStatuses] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showSettings, setShowSettings] = useState(false);
  const [editingTask, setEditingTask] = useState<DailyPlannerTask | null>(null);
  const [selectedTask, setSelectedTask] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [taskNotes, setTaskNotes] = useState<string>('');

  useEffect(() => {
    fetchTasks();
    fetchProjects();
    fetchCustomStatuses();
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchDailyTasks(selectedDate);
    }
  }, [selectedDate]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to view tasks');
        return;
      }

      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to view projects');
        return;
      }

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.id)
        .order('name');

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to view team members');
        return;
      }

      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('admin_id', user.id)
        .order('name');

      if (error) throw error;
      setTeamMembers(data || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to load team members');
    }
  };

  const fetchCustomStatuses = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to view statuses');
        return;
      }

      const { data, error } = await supabase
        .from('daily_planner_statuses')
        .select('status')
        .eq('user_id', user.id)
        .order('created_at');

      if (error) throw error;
      
      // Extract statuses from the data
      const statuses = data?.map(item => item.status) || [];
      
      // Add default statuses if none exist
      if (statuses.length === 0) {
        const defaultStatuses = ['Not Started', 'In Progress', 'Completed', 'Blocked', 'Deferred'];
        
        // Insert default statuses one by one to avoid conflicts
        for (const status of defaultStatuses) {
          await supabase.from('daily_planner_statuses').upsert({
            status,
            user_id: user.id
          });
        }
        
        setCustomStatuses(defaultStatuses);
      } else {
        setCustomStatuses(statuses);
      }
    } catch (error) {
      console.error('Error fetching custom statuses:', error);
      toast.error('Failed to load custom statuses');
      // Set default statuses if fetch fails
      setCustomStatuses(['Not Started', 'In Progress', 'Completed', 'Blocked', 'Deferred']);
    }
  };

  const fetchDailyTasks = async (date: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to view tasks');
        return;
      }

      const { data, error } = await supabase
        .from('daily_planner_tasks')
        .select('*')
        .eq('user_id', user.id) 
        .eq('date', date)
        .order('created_at');

      if (error) throw error;
      setDailyTasks(data || []);
    } catch (error) {
      console.error('Error fetching daily tasks:', error);
      toast.error('Failed to load daily tasks');
    }
  };

  const handleAddTask = async () => {
    if (!selectedTask || !selectedStatus) {
      toast.error('Please select a task and status');
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to add tasks');
        return;
      }

      const task = tasks.find(t => t.id === selectedTask);
      if (!task) {
        toast.error('Selected task not found');
        return;
      }

      // Check if task already exists for this date
      const { data: existingTasks } = await supabase
        .from('daily_planner_tasks')
        .select('*')
        .eq('user_id', user.id)
        .eq('taskid', selectedTask)
        .eq('date', selectedDate);

      // If task already exists for this date, update it instead of inserting
      if (existingTasks && existingTasks.length > 0) {
        // Update existing task
        const { data, error } = await supabase
          .from('daily_planner_tasks') 
          .update({
            status: selectedStatus,
            notes: taskNotes,
            assigned_to: selectedAssignee,
            title: task.title
          })
          .eq('id', existingTasks[0].id)
          .select()
          .single();

        if (error) throw error;
        
        if (data) {
          setDailyTasks(dailyTasks.map(t => t.id === data.id ? data : t));
          toast.success('Task updated in daily plan');
        }
      } else {
        // Insert new task
        const { data, error } = await supabase
          .from('daily_planner_tasks')
          .insert({
            user_id: user.id, 
            taskid: selectedTask, 
            title: task.title,
            status: selectedStatus,
            assigned_to: selectedAssignee,
            notes: taskNotes,
            date: selectedDate
          })
          .select()
          .single();
        if (error) throw error;
        
        if (data) {
          setDailyTasks([...dailyTasks, data]);
          toast.success('Task added to daily plan');
        }
      }
      
      setSelectedTask('');
      setSelectedStatus('');
      setSelectedAssignee('');
      setTaskNotes('');
    } catch (error) {
      console.error('Error adding task to daily plan:', error);
      toast.error('Failed to add task to daily plan');
    }
  };

  const handleUpdateTask = async () => {
    if (!editingTask) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to update tasks');
        return;
      }
      const { data, error } = await supabase
        .from('daily_planner_tasks')
        .update({
          status: editingTask.status,
          notes: editingTask.notes,
          assigned_to: editingTask.assigned_to,
          title: editingTask.title
        })
        .eq('id', editingTask.id)
        .select()
        .single();

      if (error) throw error;
      
      if (data) {
        setDailyTasks(dailyTasks.map(task => 
          task.id === editingTask.id ? data : task
        ));
      }
      setEditingTask(null);
      toast.success('Task updated');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (id: string) => {
    try {
      const { error } = await supabase
        .from('daily_planner_tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setDailyTasks(dailyTasks.filter(task => task.id !== id));
      toast.success('Task removed from daily plan');
    } catch (error) {
      console.error('Error removing task:', error);
      toast.error('Failed to remove task');
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  const getTaskById = (taskId: string) => {
    return tasks.find(task => task.id === taskId);
  };

  const getProjectForTask = (taskId: string) => {
    const task = getTaskById(taskId);
    if (!task || !task.project_id) return null;
    return projects.find(project => project.id === task.project_id);
  };

  const getTeamMemberName = (memberId?: string) => {
    if (!memberId) return null;
    const member = teamMembers.find(m => m.id === memberId);
    if (!member) return null;
    return member.name;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Daily Planner</h1>
            
            <div className="flex space-x-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <button
                onClick={() => setShowSettings(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
              >
                <Settings className="h-5 w-5 mr-2" />
                Manage Statuses
              </button>
            </div>
          </div>

          {/* Status Filter */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Filter className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by Status</h3>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter('')}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  statusFilter === '' 
                    ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200' 
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >All</button>
              {customStatuses.map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    statusFilter === status 
                      ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200' 
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  }`}
                >{status}</button>
              ))}
            </div>
          </div>

          {/* Add Task Form */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add Task to Daily Plan</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="task" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select Task
                </label>
                <select
                  id="task"
                  value={selectedTask}
                  onChange={(e) => setSelectedTask(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select a task</option>
                  {tasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Status
                </label>
                <select
                  id="status"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select a status</option>
                  {customStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="assignee" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Assign To
                </label>
                <select
                  id="assignee"
                  value={selectedAssignee}
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Unassigned</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Notes
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  value={taskNotes}
                  onChange={(e) => setTaskNotes(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Add notes for this task..."
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleAddTask}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add to Daily Plan
              </button>
            </div>
          </div>

          {/* Daily Tasks List */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                Tasks for {new Date(selectedDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </h3>
            </div>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 mx-auto"></div>
              </div>
            ) : dailyTasks.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400">No tasks planned for this day. Add tasks above to get started.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {dailyTasks.filter(task => statusFilter ? task.status === statusFilter : true).map((dailyTask) => (
                  <div key={dailyTask.id} className="p-6">
                    {editingTask && editingTask.id === dailyTask.id ? (
                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <h4 className="text-lg font-medium text-gray-900 dark:text-white">{dailyTask.title}</h4>
                          <div className="flex space-x-2">
                            <button
                              onClick={handleUpdateTask}
                              className="p-1.5 text-gray-400 hover:text-indigo-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <Save className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setEditingTask(null)}
                              className="p-1.5 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                        <div>
                          <label htmlFor={`edit-status-${dailyTask.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Status
                          </label>
                          <select
                            id={`edit-status-${dailyTask.id}`}
                            value={editingTask.status}
                            onChange={(e) => setEditingTask({...editingTask, status: e.target.value})}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            {customStatuses.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor={`edit-assignee-${dailyTask.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Assign To
                          </label>
                          <select
                            id={`edit-assignee-${dailyTask.id}`}
                            value={editingTask.assigned_to || ''}
                            onChange={(e) => setEditingTask({...editingTask, assigned_to: e.target.value || undefined})}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <option value="">Unassigned</option>
                            {teamMembers.map((member) => (
                              <option key={member.id} value={member.id}>
                                {member.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor={`edit-notes-${dailyTask.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Notes
                          </label>
                          <textarea
                            id={`edit-notes-${dailyTask.id}`}
                            rows={3}
                            value={editingTask.notes}
                            onChange={(e) => setEditingTask({...editingTask, notes: e.target.value})}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex justify-between">
                          <h4 className="text-lg font-medium text-gray-900 dark:text-white">{dailyTask.title}</h4>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setEditingTask(dailyTask)}
                              className="p-1.5 text-gray-400 hover:text-indigo-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteTask(dailyTask.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200">
                            {dailyTask.status}
                          </span>
                          {dailyTask.assigned_to && getTeamMemberName(dailyTask.assigned_to) && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200">
                              <User className="h-3 w-3 mr-1" />
                              {getTeamMemberName(dailyTask.assigned_to)}
                            </span>
                          )}
                          {getProjectForTask(dailyTask.taskid) && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200">
                              {getProjectForTask(dailyTask.taskid)?.name}
                            </span>
                          )}
                        </div>
                        {dailyTask.notes && (
                          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            <p>{dailyTask.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {showSettings && (
        <DailyPlannerSettings
          onClose={() => setShowSettings(false)}
          onStatusesUpdated={fetchCustomStatuses}
          initialStatuses={customStatuses}
        />
      )}
    </div>
  );
}